import { UserAccess } from './models';
import { mapBranchFromUserBranchDto } from '../branches/helpers';
function getInitialsFromFullName(fullName) {
    return fullName
        .split(' ')
        .filter(Boolean)
        .map(part => part[0])
        .join('')
        .toUpperCase();
}
/**
 * The profile row on its own — everything `GET /User/id/{id}` knows about a branch, which is the
 * name, the country and whether the user administers it. Used only when the fuller
 * `GET /Authentication/user/branches` row for the same id is missing, which is the state a user
 * enrolled on a branch nobody has granted them sits in.
 */
function mapUserBranchFromProfileDto(dto) {
    var _a;
    return {
        id: dto.id,
        name: dto.name,
        countryCode: dto.countryCode,
        contactPersonId: dto.axContactPersonId,
        axCustomerId: dto.axCustomerId,
        access: ((_a = dto.permissions) === null || _a === void 0 ? void 0 : _a.admin) ? UserAccess.Admin : UserAccess.Regular,
    };
}
/**
 * The two branch reads merged, exactly as the pre-Efkt `AuthApi.getAuthenticatedUser` merges them:
 * the profile decides *which* branches the session has and who administers them, the session read
 * supplies the settings and address the UI renders. The profile wins on `countryCode` because
 * retail-core's own merge did — see `datahub/auth/index.ts`.
 */
function mergeUserBranches(profileBranches = [], accountBranches = []) {
    return profileBranches.map(profileBranch => {
        const account = accountBranches.find(({ id }) => id === profileBranch.id);
        const fallback = mapUserBranchFromProfileDto(profileBranch);
        if (!account)
            return fallback;
        return {
            ...mapBranchFromUserBranchDto(account),
            ...fallback,
            countryCode: profileBranch.countryCode || account.country,
        };
    });
}
/**
 * The signed-in session, from the three reads that make it up: the token's claims
 * (`GET /Authentication/user`), the branches the user acts for
 * (`GET /Authentication/user/branches`) and the profile (`GET /User/id/{id}`).
 *
 * `isInternalUser` inverts `isFromEsoft` because that is what the pre-Efkt mapper does and what
 * every component reading it expects — the name is wrong in both trees, and fixing it is not this
 * ticket's job. Note Efkt always sends `isFromEsoft: false` on the customer route, so an employee
 * session still resolves through datahub.
 *
 * The `currentlyImpersonating*` overlay is kept for contract parity only: Efkt never honours
 * `ESOFT-IMPERSONATING-USER-ID`, so those fields are always null there and this collapses to the
 * caller's own identity.
 */
export function mapAuthenticatedUserFromDtos(currentUser, profile, accountBranches) {
    var _a;
    const { currentlyImpersonatingName, currentlyImpersonatingEmail, currentlyImpersonatingUserId } = currentUser;
    const firstName = currentlyImpersonatingName
        ? currentlyImpersonatingName.split(' ')[0]
        : currentUser.firstName || profile.firstName || '';
    const lastName = currentlyImpersonatingName
        ? currentlyImpersonatingName.substring(firstName.length).trim()
        : currentUser.lastName || profile.lastName || '';
    const fullName = currentlyImpersonatingName || currentUser.fullName || `${firstName} ${lastName}`.trim();
    return {
        _dto: profile,
        id: currentlyImpersonatingUserId || currentUser.id,
        firstName,
        lastName,
        fullName,
        initials: getInitialsFromFullName(fullName),
        email: currentlyImpersonatingEmail || currentUser.email || profile.email || '',
        phoneNumber: profile.phone,
        visiblePrices: !!((_a = profile.settings) === null || _a === void 0 ? void 0 : _a.showPrices),
        isInternalUser: !currentUser.isFromEsoft,
        branches: mergeUserBranches(profile.branches, accountBranches),
        sourceBackend: 'efkt',
    };
}
/** `GET /User/id/{id}` on its own, for the users-administration lookup rather than the session. */
export function mapUserFromDto(dto) {
    var _a;
    const fullName = `${dto.firstName || ''} ${dto.lastName || ''}`.trim();
    return {
        _dto: dto,
        id: dto.id,
        firstName: dto.firstName || '',
        lastName: dto.lastName || '',
        fullName,
        initials: getInitialsFromFullName(fullName),
        email: dto.email || '',
        phoneNumber: dto.phone,
        visiblePrices: !!((_a = dto.settings) === null || _a === void 0 ? void 0 : _a.showPrices),
        // `GET /User/id/{id}` says nothing about employees; only `GET /Authentication/user` does.
        isInternalUser: false,
        branches: (dto.branches || []).map(mapUserBranchFromProfileDto),
        sourceBackend: 'efkt',
    };
}
export function mapUserBranchAccessFromDto(dto, branchId) {
    return {
        id: dto.id,
        fullName: `${dto.firstName} ${dto.lastName}`.trim(),
        email: dto.email,
        phoneNumber: dto.phone,
        branchId,
        access: dto.isAdmin ? UserAccess.Admin : UserAccess.Regular,
        sourceBackend: 'efkt',
    };
}
